import { Group, Rect } from 'react-konva';
import React, { useCallback, useMemo } from 'react';
import { setTempState, useTempState } from '@starflow/app/store';

const boundaryColor = '#00000060';

export const ElementWrapper = React.memo((props) => {
  const { element, groupId, config = {}, draggable, children, selectionMap, onDragEnd } = props;

  const showBoundary = useMemo(() => {
    if (!selectionMap[element.id]) return false;
    if (Object.keys(selectionMap).length === 1) return false;

    return true;
  }, [element.id, selectionMap]);

  const dragEnhanced = !!selectionMap[element.id] || showBoundary;

  const handleMouseOver = useCallback(() => {
    clearTimeout(window.mouseLeaveTimer);
    setTempState({ hoveringElementId: element.id });
  }, [element.id]);

  const handleMouseOut = useCallback(() => {
    clearTimeout(window.mouseLeaveTimer);
    window.mouseLeaveTimer = setTimeout(() => {
      setTempState({ hoveringElementId: null });
    }, 100);
  }, []);

  const handleDragStart = useCallback(() => {
    setTempState({ [`element-${element.id}-transforming`]: true });
  }, []);

  const handleDragEnd = useCallback(
    (event) => {
      setTempState({ [`element-${element.id}-transforming`]: false });
      onDragEnd && onDragEnd(element, event.target.attrs);
    },
    [element, onDragEnd],
  );

  return (
    <Group
      name={element.id}
      x={element.x}
      y={element.y}
      role="element"
      id={`${element.id}-wrapper`}
      draggingIcon={element.url}
      roleType={element.type}
      scaleX={element.scaleX}
      scaleY={element.scaleY}
      draggable={draggable}
      rotation={element.rotation}
      unselectable={config.unselectable}
      groupId={groupId}
      onMouseOver={handleMouseOver}
      onMouseOut={handleMouseOut}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onTransformStart={handleDragStart}
      onTransformEnd={handleDragEnd}
    >
      <Rect
        strokeWidth={1}
        visible={dragEnhanced}
        width={element.width}
        height={element.height}
        listening={dragEnhanced}
        strokeScaleEnabled={false}
        stroke={showBoundary ? boundaryColor : '#0000'}
      />
      {children}
    </Group>
  );
});

export const OverlayerWrapper = React.memo((props) => {
  const { element, viewport, children, isMiniMode } = props;
  const transforming = useTempState(`element-${element.id}-transforming`);

  const left = element.x * viewport.scale + viewport.x;
  let top = element.y * viewport.scale + viewport.y;
  const width = element.width * element.scaleX * viewport.scale;
  const height = element.height * element.scaleY * viewport.scale;

  const handleMouseOver = useCallback(() => {
    clearTimeout(window.mouseLeaveTimer);
    setTempState({ hoveringElementId: element.id });
  }, [element.id]);

  const handleMouseOut = useCallback(() => {
    clearTimeout(window.mouseLeaveTimer);
    window.mouseLeaveTimer = setTimeout(() => {
      setTempState({ hoveringElementId: null });
    }, 100);
  }, []);

  return (
    <div
      style={{ top, left, width, height, opacity: transforming ? 0 : 1 }}
      data-role="overlayer-wrapper"
      data-mini-mode={isMiniMode}
      data-element-id={element.id}
      onMouseOver={handleMouseOver}
      onMouseOut={handleMouseOut}
      className="absolute pointer-events-none"
    >
      {children}
    </div>
  );
});
